import { apiEmployees } from "@/api/employees/employee.api";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeCustomTableKeys } from "@/queries/employees/use-employee-custom-table.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmployeeAssignPositionPayload } from "@/types/employees/employee-position.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeePositionsKeys = createQueryKeys("employeePositions", {
  search: (employeeId: number, pageNumber: number) => ({
    queryKey: [employeeId, pageNumber],
    queryFn: async () => {
      const result = await apiEmployees.positionSearch(employeeId, pageNumber);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeePositionsSearch = (
  employeeId: number,
  page: number,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeePositionsKeys.search(employeeId, page),
    enabled: enabled,
  });
};

export interface SaveParams {
  employeeId: number;
  positionId: number | undefined;
  payload: ApiEmployeeAssignPositionPayload;
}

export const useEmployeePositionSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ employeeId, positionId, payload }: SaveParams) => {
      const result = !positionId
        ? await apiEmployees.positionCreate(employeeId, payload)
        : await apiEmployees.positionUpdate(employeeId, positionId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        // todo обнулять только нужного сотрудника
        queryKey: employeePositionsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(variables.employeeId, 0, {}).queryKey.slice(0, 3),
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeePositionDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, positionId }: { employeeId: number; positionId: number }) => {
      const result = await apiEmployees.positionDelete(employeeId, positionId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: employeePositionsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeesKeys.detail(variables.employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(variables.employeeId, 0, {}).queryKey.slice(0, 3),
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
